import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Trash2,
  Calendar,
  Loader2,
  Globe,
  CheckCircle,
  XCircle,
  UserPlus,
  Unlink,
  Clock,
  X,
  Save,
  Timer,
  Shield,
  Zap,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { calendarService } from '../../services/calendarService';
import { DayScheduleRow } from './DayScheduleRow';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';
import type { CalendarProvider, AvailabilitySettings } from '../../types/calendar';

interface SalesTeamMember {
  id: string;
  client_id: string;
  name: string;
  email: string;
  markets: MarketConfig[];
  priority: number;
  meetings_assigned_count: number;
  last_assigned_at: string | null;
  is_active: boolean;
  in_admin_pool: boolean;
}

interface MarketConfig {
  name: string;
  regions: string[];
  countries: string[];
}

interface CalendarConnectionInfo {
  id: string;
  provider: CalendarProvider;
  calendar_email: string;
  timezone: string;
  is_active: boolean;
  created_at: string;
}

const STORAGE_KEYS: Record<CalendarProvider, string> = {
  google: 'oauth-result-calendar',
  microsoft: 'oauth-result-microsoft-calendar',
};

const COUNTRY_OPTIONS: { value: string; label: string; aliases: string[] }[] = [
  { value: 'UAE', label: 'UAE', aliases: ['united arab emirates', 'uae'] },
  { value: 'UK', label: 'UK', aliases: ['united kingdom', 'uk', 'great britain'] },
  { value: 'USA', label: 'USA', aliases: ['united states', 'usa', 'us'] },
  { value: 'Australia', label: 'Australia', aliases: ['australia'] },
  { value: 'Singapore', label: 'Singapore', aliases: ['singapore'] },
  { value: 'Canada', label: 'Canada', aliases: ['canada'] },
];

const TIMEZONES = [
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Zurich',
  'Europe/Vienna',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Helsinki',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Athens',
  'Europe/Bucharest',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Edmonton',
  'America/Winnipeg',
  'America/Halifax',
  'America/St_Johns',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Hobart',
  'UTC',
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

export function SalesTeamSection() {
  const { user } = useAuth();
  const [members, setMembers] = useState<SalesTeamMember[]>([]);
  const [connections, setConnections] = useState<Record<string, CalendarConnectionInfo | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addingMember, setAddingMember] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [connectingMemberId, setConnectingMemberId] = useState<string | null>(null);
  const [editingMarketsId, setEditingMarketsId] = useState<string | null>(null);
  const [editingAvailabilityId, setEditingAvailabilityId] = useState<string | null>(null);
  const [togglingRoundRobinId, setTogglingRoundRobinId] = useState<string | null>(null);

  const loadMembers = async () => {
    if (!user?.client_id) return;
    try {
      setLoading(true);
      const data = await calendarService.getSalesTeamMembers(user.client_id);
      setMembers(data);

      const connMap: Record<string, CalendarConnectionInfo | null> = {};
      for (const member of data) {
        const conn = await calendarService.getCalendarConnectionForMember(user.client_id, member.id);
        connMap[member.id] = conn;
      }
      setConnections(connMap);
    } catch (err) {
      console.error('Error loading sales team:', err);
      setError('Failed to load sales team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, [user?.client_id]);

  const handleAddMember = async () => {
    if (!user?.client_id || !newName.trim() || !newEmail.trim()) return;
    setError(null);

    const result = await calendarService.addSalesTeamMember({
      client_id: user.client_id,
      name: newName.trim(),
      email: newEmail.trim(),
      markets: [],
    });

    if (result.success) {
      setNewName('');
      setNewEmail('');
      setAddingMember(false);
      await loadMembers();
    } else {
      setError(result.error || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Remove this team member? Their calendar connection will be deactivated.')) return;

    const conn = connections[memberId];
    if (conn && user?.client_id) {
      await calendarService.disconnectMemberCalendar(user.client_id, memberId, conn.provider);
    }

    const result = await calendarService.removeSalesTeamMember(memberId);
    if (result.success) {
      await loadMembers();
    } else {
      setError(result.error || 'Failed to remove member');
    }
  };

  const handleConnectCalendar = async (memberId: string, provider: CalendarProvider) => {
    if (!user?.client_id) return;
    setConnectingMemberId(memberId);
    setError(null);

    const storageKey = STORAGE_KEYS[provider];
    localStorage.removeItem(storageKey);

    try {
      let authUrl: string;
      if (provider === 'google') {
        authUrl = await calendarService.initiateGoogleOAuthForMember(user.client_id, user.email || '', memberId);
      } else {
        authUrl = await calendarService.initiateMicrosoftOAuthForMember(user.client_id, user.email || '', memberId);
      }

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        authUrl,
        `${provider}-calendar-oauth-popup`,
        `width=${width},height=${height},left=${left},top=${top}`
      );

      let timeoutId: number | null = null;
      let pollId: number | null = null;

      const cleanup = () => {
        if (timeoutId) clearTimeout(timeoutId);
        if (pollId) clearInterval(pollId);
      };

      pollId = window.setInterval(() => {
        if (popup && popup.closed) {
          const raw = localStorage.getItem(storageKey);
          if (!raw) {
            cleanup();
            setConnectingMemberId(null);
            return;
          }
        }

        const raw = localStorage.getItem(storageKey);
        if (!raw) return;

        cleanup();
        localStorage.removeItem(storageKey);

        try {
          const data = JSON.parse(raw);
          if (data.success) {
            loadMembers();
          } else {
            setError(data.error || 'Failed to connect calendar');
          }
        } catch {
          setError('Failed to connect calendar');
        }
        setConnectingMemberId(null);
      }, 500);

      timeoutId = window.setTimeout(() => {
        cleanup();
        setConnectingMemberId(null);
        setError('Connection timed out. Please try again.');
      }, 120000);
    } catch (err) {
      console.error('Error initiating OAuth:', err);
      setError('Failed to start connection process.');
      setConnectingMemberId(null);
    }
  };

  const handleDisconnectCalendar = async (memberId: string) => {
    if (!user?.client_id) return;
    const conn = connections[memberId];
    if (!conn) return;
    if (!confirm('Disconnect this calendar?')) return;

    await calendarService.disconnectMemberCalendar(user.client_id, memberId, conn.provider);
    await loadMembers();
  };

  const handleToggleRoundRobin = async (memberId: string, currentValue: boolean) => {
    setTogglingRoundRobinId(memberId);
    const success = await calendarService.toggleMemberAdminPool(memberId, !currentValue);
    if (success) {
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, in_admin_pool: !currentValue } : m));
    }
    setTogglingRoundRobinId(null);
  };

  const handleUpdateMarkets = async (memberId: string, markets: MarketConfig[]) => {
    const result = await calendarService.updateSalesTeamMember(memberId, { markets });
    if (result.success) {
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, markets } : m));
      setEditingMarketsId(null);
    } else {
      setError(result.error || 'Failed to update markets');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {members.length > 0 && members.every(m => !connections[m.id] || !connections[m.id]?.is_active) && (
        <div className="p-4 bg-red-50 rounded-xl border border-red-300 flex items-start gap-3">
          <Calendar className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">No calendars connected</p>
            <p className="text-sm text-red-600 mt-0.5">
              None of your sales team members have an active calendar connection. Connect a calendar to enable smart scheduling and availability routing.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Sales Team</h3>
              <p className="text-sm text-gray-500">Add team members and connect their calendars for smart routing</p>
            </div>
          </div>
          <button
            onClick={() => setAddingMember(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white text-sm font-semibold rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
          >
            <Plus className="w-4 h-4" />
            Add Member
          </button>
        </div>

        {addingMember && (
          <div className="p-5 border-b border-gray-100 bg-gray-50">
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1875FF]/20 focus:border-[#1875FF] outline-none"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="john@company.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1875FF]/20 focus:border-[#1875FF] outline-none"
                />
              </div>
              <button
                onClick={handleAddMember}
                disabled={!newName.trim() || !newEmail.trim()}
                className="px-4 py-2 bg-[#1875FF] text-white text-sm font-medium rounded-lg hover:bg-[#1260d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add
              </button>
              <button
                onClick={() => { setAddingMember(false); setNewName(''); setNewEmail(''); }}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {members.length === 0 && !addingMember ? (
          <div className="p-8 text-center">
            <UserPlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500 mb-1">No sales team members yet</p>
            <p className="text-xs text-gray-400">Add team members to enable multi-calendar routing and round-robin scheduling</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {members.map((member) => (
              <MemberRow
                key={member.id}
                member={member}
                connection={connections[member.id] || null}
                isConnecting={connectingMemberId === member.id}
                isEditingMarkets={editingMarketsId === member.id}
                isTogglingRoundRobin={togglingRoundRobinId === member.id}
                onConnect={handleConnectCalendar}
                onDisconnect={handleDisconnectCalendar}
                onRemove={handleRemoveMember}
                onEditMarkets={() => setEditingMarketsId(editingMarketsId === member.id ? null : member.id)}
                onEditAvailability={() => setEditingAvailabilityId(member.id)}
                onSaveMarkets={handleUpdateMarkets}
                onToggleRoundRobin={handleToggleRoundRobin}
              />
            ))}
          </div>
        )}
      </div>


      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-gray-900 font-display mb-1">Smart Scheduling</h4>
            <p className="text-xs text-gray-600">
              SalesPad will automatically check availability and schedule meetings without conflicts.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-[#1875FF] flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-gray-900 font-display mb-1">How Multi-Calendar Routing Works</h4>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>Meetings are routed based on the prospect's timezone/market to the right team member</li>
              <li>If a member is fully booked for the next 3 days, the system falls back to the next available member</li>
              <li>Meetings are distributed evenly (round-robin) between members covering the same market</li>
            </ul>
          </div>
        </div>
      </div>

      {editingAvailabilityId && user?.client_id && (
        <AvailabilityModal
          clientId={user.client_id}
          memberId={editingAvailabilityId}
          memberName={members.find(m => m.id === editingAvailabilityId)?.name || ''}
          memberTimezone={connections[editingAvailabilityId]?.timezone}
          onClose={() => setEditingAvailabilityId(null)}
        />
      )}
    </div>
  );
}

function MemberRow({
  member,
  connection,
  isConnecting,
  isEditingMarkets,
  isTogglingRoundRobin,
  onConnect,
  onDisconnect,
  onRemove,
  onEditMarkets,
  onEditAvailability,
  onSaveMarkets,
  onToggleRoundRobin,
}: {
  member: SalesTeamMember;
  connection: CalendarConnectionInfo | null;
  isConnecting: boolean;
  isEditingMarkets: boolean;
  isTogglingRoundRobin: boolean;
  onConnect: (memberId: string, provider: CalendarProvider) => void;
  onDisconnect: (memberId: string) => void;
  onRemove: (memberId: string) => void;
  onEditMarkets: () => void;
  onEditAvailability: () => void;
  onSaveMarkets: (memberId: string, markets: MarketConfig[]) => void;
  onToggleRoundRobin: (memberId: string, currentValue: boolean) => void;
}) {
  return (
    <div className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center">
            <span className="text-sm font-bold text-gray-600">
              {member.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{member.name}</p>
            <p className="text-xs text-gray-500">{member.email}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {connection ? (
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${connection.is_active ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                <span className={`w-2 h-2 rounded-full animate-pulse ${connection.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`text-xs font-medium ${connection.is_active ? 'text-green-700' : 'text-red-700'}`}>
                  {connection.is_active ? (connection.provider === 'google' ? 'Google' : 'Outlook') : 'Not connected'}
                </span>
              </div>
              <button
                onClick={() => onDisconnect(member.id)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                title="Disconnect calendar"
              >
                <Unlink className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border bg-red-50 border-red-200">
                <span className="w-2 h-2 rounded-full animate-pulse bg-red-500" />
                <span className="text-xs font-medium text-red-700">Not connected</span>
              </div>
              {isConnecting ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-500" />
                  <span className="text-xs text-gray-500">Connecting...</span>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => onConnect(member.id, 'google')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <img src="/Google-Calendar-icon.svg" alt="Google" className="w-4 h-4" />
                    Google
                  </button>
                  <button
                    onClick={() => onConnect(member.id, 'microsoft')}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  >
                    <img src="/outlook-calendar-icon.svg" alt="Outlook" className="w-4 h-4" />
                    Outlook
                  </button>
                </>
              )}
            </div>
          )}

          <button
            onClick={onEditAvailability}
            className="p-1.5 rounded-md transition-colors text-gray-400 hover:text-[#1875FF] hover:bg-blue-50"
            title="Availability hours"
          >
            <Clock className="w-4 h-4" />
          </button>

          <button
            onClick={onEditMarkets}
            className={`p-1.5 rounded-md transition-colors ${
              isEditingMarkets
                ? 'text-[#1875FF] bg-blue-50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
            title="Edit markets"
          >
            <Globe className="w-4 h-4" />
          </button>

          <button
            onClick={() => onRemove(member.id)}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
            title="Remove member"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {member.markets.length > 0 && !isEditingMarkets && (
        <div className="mt-2.5 flex flex-wrap gap-1.5 ml-12">
          {member.markets.map((market, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-md border border-blue-100"
            >
              {market.name}
            </span>
          ))}
          <span className="text-xs text-gray-400 self-center ml-1">
            {member.meetings_assigned_count} meetings assigned
          </span>
        </div>
      )}

      {!isEditingMarkets && (
        <div className="mt-2.5 ml-12 flex items-center gap-3">
          <span className="text-xs font-medium text-gray-600">Include in round-robin</span>
          <button
            type="button"
            onClick={() => onToggleRoundRobin(member.id, member.in_admin_pool)}
            disabled={isTogglingRoundRobin}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#1875FF]/50 focus:ring-offset-2 ${
              member.in_admin_pool
                ? 'bg-gradient-to-r from-[#1875FF] to-[#05E0D1]'
                : 'bg-gray-300'
            } ${isTogglingRoundRobin ? 'opacity-50' : ''}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform duration-200 ${
                member.in_admin_pool ? 'translate-x-[22px]' : 'translate-x-[2px]'
              }`}
            />
          </button>
          <span className="text-xs text-gray-400">
            {member.in_admin_pool ? 'In pool' : 'Own leads only'}
          </span>
        </div>
      )}

      {isEditingMarkets && (
        <MarketEditor
          markets={member.markets}
          onSave={(markets) => onSaveMarkets(member.id, markets)}
          onCancel={onEditMarkets}
        />
      )}
    </div>
  );
}

function AvailabilityModal({
  clientId,
  memberId,
  memberName,
  memberTimezone,
  onClose,
}: {
  clientId: string;
  memberId: string;
  memberName: string;
  memberTimezone?: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState<Partial<AvailabilitySettings>>({
    timezone: memberTimezone || 'Europe/London',
    slot_duration: 30,
    buffer_time: 0,
    monday_start: '10:00',
    monday_end: '17:00',
    is_day_enabled_monday: true,
    tuesday_start: '10:00',
    tuesday_end: '17:00',
    is_day_enabled_tuesday: true,
    wednesday_start: '10:00',
    wednesday_end: '17:00',
    is_day_enabled_wednesday: true,
    thursday_start: '10:00',
    thursday_end: '17:00',
    is_day_enabled_thursday: true,
    friday_start: '10:00',
    friday_end: '17:00',
    is_day_enabled_friday: true,
    saturday_start: '10:00',
    saturday_end: '17:00',
    is_day_enabled_saturday: false,
    sunday_start: '10:00',
    sunday_end: '17:00',
    is_day_enabled_sunday: false,
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const data = await calendarService.getAvailabilitySettingsForMember(clientId, memberId);
        if (data) {
          setSettings({
            ...data,
            monday_start: data.monday_start.substring(0, 5),
            monday_end: data.monday_end.substring(0, 5),
            tuesday_start: data.tuesday_start.substring(0, 5),
            tuesday_end: data.tuesday_end.substring(0, 5),
            wednesday_start: data.wednesday_start.substring(0, 5),
            wednesday_end: data.wednesday_end.substring(0, 5),
            thursday_start: data.thursday_start.substring(0, 5),
            thursday_end: data.thursday_end.substring(0, 5),
            friday_start: data.friday_start.substring(0, 5),
            friday_end: data.friday_end.substring(0, 5),
            saturday_start: data.saturday_start.substring(0, 5),
            saturday_end: data.saturday_end.substring(0, 5),
            sunday_start: data.sunday_start.substring(0, 5),
            sunday_end: data.sunday_end.substring(0, 5),
          });
        }
      } catch (err) {
        console.error('Error loading member availability:', err);
        setError('Failed to load availability settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [clientId, memberId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const result = await calendarService.saveAvailabilitySettingsForMember(
        {
          ...settings,
          client_id: clientId,
          user_id: user?.email || '',
          monday_start: settings.monday_start + ':00',
          monday_end: settings.monday_end + ':00',
          tuesday_start: settings.tuesday_start + ':00',
          tuesday_end: settings.tuesday_end + ':00',
          wednesday_start: settings.wednesday_start + ':00',
          wednesday_end: settings.wednesday_end + ':00',
          thursday_start: settings.thursday_start + ':00',
          thursday_end: settings.thursday_end + ':00',
          friday_start: settings.friday_start + ':00',
          friday_end: settings.friday_end + ':00',
          saturday_start: settings.saturday_start + ':00',
          saturday_end: settings.saturday_end + ':00',
          sunday_start: settings.sunday_start + ':00',
          sunday_end: settings.sunday_end + ':00',
        },
        memberId
      );

      if (result.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to save settings');
      }
    } catch (err) {
      console.error('Error saving availability:', err);
      setError('Failed to save availability settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900 font-display">Availability Hours</h3>
              <p className="text-sm text-gray-500">{memberName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#1875FF] animate-spin" />
            </div>
          ) : (
            <>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-green-700">Settings saved successfully!</p>
                </div>
              )}

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Globe className="w-4 h-4 text-[#1875FF]" />
                  Timezone
                </label>
                <SimpleCustomSelect
                  value={settings.timezone || 'Europe/London'}
                  onChange={(value) => setSettings({ ...settings, timezone: value })}
                  options={TIMEZONES.map(t => ({ value: t, label: t.replace(/_/g, ' ') }))}
                  placeholder="Select timezone"
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 text-[#1875FF]" />
                  Weekly Schedule
                </label>
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  {DAYS.map((day) => (
                    <DayScheduleRow
                      key={day}
                      day={DAY_LABELS[day]}
                      enabled={settings[`is_day_enabled_${day}` as keyof typeof settings] as boolean}
                      startTime={settings[`${day}_start` as keyof typeof settings] as string}
                      endTime={settings[`${day}_end` as keyof typeof settings] as string}
                      onEnabledChange={(enabled) =>
                        setSettings({ ...settings, [`is_day_enabled_${day}`]: enabled })
                      }
                      onStartTimeChange={(time) =>
                        setSettings({ ...settings, [`${day}_start`]: time })
                      }
                      onEndTimeChange={(time) =>
                        setSettings({ ...settings, [`${day}_end`]: time })
                      }
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Timer className="w-4 h-4 text-[#1875FF]" />
                    Meeting Duration
                  </label>
                  <SimpleCustomSelect
                    value={String(settings.slot_duration || 30)}
                    onChange={(value) =>
                      setSettings({ ...settings, slot_duration: Number(value) })
                    }
                    options={[
                      { value: '15', label: '15 minutes' },
                      { value: '30', label: '30 minutes' },
                      { value: '45', label: '45 minutes' },
                      { value: '60', label: '60 minutes' },
                    ]}
                    placeholder="Select duration"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Shield className="w-4 h-4 text-[#1875FF]" />
                    Buffer Time
                  </label>
                  <SimpleCustomSelect
                    value={String(settings.buffer_time ?? 0)}
                    onChange={(value) =>
                      setSettings({ ...settings, buffer_time: Number(value) })
                    }
                    options={[
                      { value: '0', label: 'No buffer' },
                      { value: '5', label: '5 minutes' },
                      { value: '10', label: '10 minutes' },
                      { value: '15', label: '15 minutes' },
                      { value: '30', label: '30 minutes' },
                    ]}
                    placeholder="Select buffer time"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {!loading && (
          <div className="p-5 border-t border-gray-200 shrink-0">
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full px-4 py-2.5 bg-gradient-to-r from-[#1875FF] to-[#05E0D1] text-white font-medium rounded-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Availability</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketEditor({
  markets,
  onSave,
  onCancel,
}: {
  markets: MarketConfig[];
  onSave: (markets: MarketConfig[]) => void;
  onCancel: () => void;
}) {
  const [selectedCountries, setSelectedCountries] = useState<string[]>(() => {
    if (markets.length > 0) {
      const countryValues: string[] = [];
      for (const market of markets) {
        const opt = COUNTRY_OPTIONS.find(c =>
          c.aliases.some(a => market.countries.includes(a)) || c.value === market.name
        );
        if (opt && !countryValues.includes(opt.value)) {
          countryValues.push(opt.value);
        }
      }
      return countryValues;
    }
    return [];
  });

  const toggleCountry = (countryValue: string) => {
    setSelectedCountries(prev =>
      prev.includes(countryValue)
        ? prev.filter(c => c !== countryValue)
        : [...prev, countryValue]
    );
  };

  const handleSave = () => {
    const marketConfigs: MarketConfig[] = selectedCountries.map(countryValue => {
      const opt = COUNTRY_OPTIONS.find(c => c.value === countryValue)!;
      return {
        name: opt.label,
        regions: [],
        countries: opt.aliases,
      };
    });
    onSave(marketConfigs);
  };

  return (
    <div className="mt-3 ml-12 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
      <p className="text-xs font-medium text-gray-600">Country Assignments</p>
      <p className="text-xs text-gray-400">Select which countries this member should handle</p>
      <div className="flex flex-wrap gap-2">
        {COUNTRY_OPTIONS.map(opt => (
          <button
            key={opt.value}
            onClick={() => toggleCountry(opt.value)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
              selectedCountries.includes(opt.value)
                ? 'bg-[#1875FF] text-white border-[#1875FF] shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={selectedCountries.length === 0}
          className="px-3 py-1.5 text-xs font-medium text-white bg-[#1875FF] rounded-md hover:bg-[#1260d9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Markets
        </button>
      </div>
    </div>
  );
}
